import update from 'immutability-helper';
import { ActionType, defaultAction } from 'src/redux/types';
import { getTranslation } from 'src/utilities/translator';
import {
  DEFAULT_ADDTIONAL_DOCUMENT_STATE,
  DEFAULT_INVOICE_STATE,
  DEFAULT_SUB_STEP_VALUE,
  MAXIMUM_ATTEMPTS,
} from '../Create/constant';
import { AdditionalDocument, SubStepValue } from '../Create/model';
import { ACTION_TYPES } from './types';

const initialState: InternationalRemitList = {
  currentPage: 0,
  subPage: DEFAULT_SUB_STEP_VALUE,
  submittedID: '',
  document: {
    count: 0,
    totalSizes: 0,
    last_quotation_id: '',
  },
  invoice: DEFAULT_INVOICE_STATE,
  additionalDocument: DEFAULT_ADDTIONAL_DOCUMENT_STATE,
  transferDetail: {
    source_of_fund: 'BI',
    purpose_of_transfer: 'BE',
  },
  sender: {
    ref: '',
    type: '',
    data: {},
  },
  recipient: {
    ref: '',
    type: '',
    data: {},
    bank: {},
  },
  quotation: {
    tx_id: '',
    source_amount: '0',
    destination_amount: '000',
    converted_amount: '0',
    fx_rate: '0',
    transfer_fee: '0',
    last_updated: '',
    created_time: '',
    expiry_time: '',
    destination_currency_code: '',
    destination_country_code: '',
    destination_country_label: '',
    destination_currency_label: '',
    maximum_amount: 35000000,
    is_using_swift: false,
    is_business_recipient_only: false,
  },
  attempts: MAXIMUM_ATTEMPTS,
  column: {
    displayedColumns: [
      {
        key: 'created',
        label: getTranslation('common_dateAndTime'),
      },
      {
        key: 'id',
        label: getTranslation('common_transactionID'),
      },
      {
        key: 'sender_name',
        label: getTranslation('common_senderName'),
      },
      {
        key: 'amount_send',
        label: getTranslation('common_amountSend'),
      },
      {
        key: 'recipient_name',
        label: getTranslation('common_recipientName'),
      },
      {
        key: 'amount_received',
        label: getTranslation('common_amountReceived'),
      },
      {
        key: 'status',
        label: getTranslation('common_status'),
      },
      {
        key: 'action',
        label: getTranslation('common_action'),
      },
      {
        key: 'created_by',
        label: getTranslation('common_createdBy'),
      },
    ],
    showCustomColumnModal: false,
  },
};

const updateExistingFileByKey = (
  files: AdditionalDocument[],
  newFile: AdditionalDocument,
) => {
  return files.map(file => {
    if (file.key === newFile.key) {
      return newFile;
    } else {
      return file;
    }
  });
};

export type ColumnConfigSchema = {
  key: string;
  label: string;
};

export type ConfigSchemaData = {
  columns: Array<ColumnConfigSchema>;
  schema: string;
};

export type TransferDetail = {
  source_of_fund: string;
  purpose_of_transfer: string;
};

export type Sender = {
  ref: string;
  type: string;
  data: object;
};

export type Recipient = {
  ref: string;
  type: string;
  data: object;
  bank: object;
};

export type Document = {
  count: number;
  totalSizes: number;
  last_quotation_id: string;
};

export type Quotation = {
  tx_id: string;
  source_amount: string;
  destination_amount: string;
  converted_amount: string;
  fx_rate: string;
  transfer_fee: string;
  last_updated: string;
  created_time: string;
  expiry_time: string;
  destination_currency_code: string;
  destination_currency_label: string;
  destination_country_code: string;
  destination_country_label: string;
  maximum_amount: number;
  is_using_swift: boolean;
  is_business_recipient_only: boolean;
};

export type Column = {
  displayedColumns: ConfigSchemaData['columns'];
  showCustomColumnModal: boolean;
};

export type InternationalRemitList = {
  currentPage: number;
  submittedID: string;
  attempts: number;
  subPage: SubStepValue;
  document: Document;
  invoice: AdditionalDocument | null;
  additionalDocument: AdditionalDocument[];
  transferDetail: TransferDetail;
  sender: Sender;
  recipient: Recipient;
  quotation: Quotation;
  column: Column;
};

const internationalRemitReducer = (
  state = initialState,
  action: ActionType = defaultAction,
) => {
  const { payload, type } = action;

  switch (type) {
    case ACTION_TYPES.INTERNATIONAL_REMIT_RESET_CREATE_STATE:
      return {
        ...state,
        currentPage: initialState.currentPage,
        submittedID: initialState.submittedID,
        attempts: initialState.attempts,
        subPage: initialState.subPage,
        document: initialState.document,
        invoice: initialState.invoice,
        additionalDocument: initialState.additionalDocument,
        transferDetail: initialState.transferDetail,
        sender: initialState.sender,
        recipient: initialState.recipient,
        quotation: initialState.quotation,
      };
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_PAGE:
      return update(state, {
        currentPage: {
          $set: payload,
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_SUB_PAGE:
      return update(state, {
        subPage: {
          $set: payload,
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_ATTEMPTS:
      return update(state, {
        attempts: {
          $set: payload,
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_ADD_DOCUMENT:
      return update(state, {
        additionalDocument: {
          $push: payload,
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_EDIT_DOCUMENT:
      return {
        ...state,
        additionalDocument: updateExistingFileByKey(
          state.additionalDocument,
          payload,
        ),
      };
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_DELETE_DOCUMENT:
      return update(state, {
        additionalDocument: {
          $set: payload,
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_INVOICE:
      return update(state, {
        invoice: {
          $set: payload,
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_DOCUMENT_STATE:
      return update(state, {
        document: {
          $set: payload,
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_SENDER_TYPE:
      return update(state, {
        sender: {
          type: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_SUBMITTED_ID:
      return update(state, {
        submittedID: {
          $set: payload,
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_RECIPIENT_TYPE:
      return update(state, {
        recipient: {
          type: {
            $set: payload,
          },
        },
      });

    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_SENDER_REF:
      return update(state, {
        sender: {
          ref: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_RECIPIENT_REF:
      return update(state, {
        recipient: {
          ref: {
            $set: payload,
          },
        },
      });

    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_SENDER_DATA:
      return update(state, {
        sender: {
          data: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_RECIPIENT_DATA:
      return update(state, {
        recipient: {
          data: {
            $set: payload,
          },
        },
      });

    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_RECIPIENT_BANK:
      return update(state, {
        recipient: {
          bank: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_QUOTATION:
      return update(state, {
        quotation: {
          $merge: payload,
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_CREATE_UPDATE_TRANSFER_DETAIL:
      return update(state, {
        transferDetail: {
          $set: payload,
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_LIST_COLUMN:
      return update(state, {
        column: {
          displayedColumns: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.INTERNATIONAL_REMIT_LIST_SHOW_MODAL:
      return update(state, {
        column: {
          showCustomColumnModal: {
            $set: payload,
          },
        },
      });
    default:
      return state;
  }
};

export default internationalRemitReducer;
